import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { 
  Search, 
  FileText, 
  Filter, 
  RotateCcw, 
  BookOpen, 
  ShieldCheck, 
  Layers, 
  ChevronRight, 
  ChevronLeft,
  Printer,
  AlertTriangle,
  Building,
  Calendar,
  Sparkles,
  ArrowUpDown,
  QrCode,
  Copy,
  Check,
  X,
  LayoutGrid,
  List,
  Table,
  ArrowDown,
  ArrowUp
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import DocumentDetailModal from '../components/DocumentDetailModal';
import { BlacksLawDictionaryModal } from '../components/BlacksLawDictionaryModal';
import { useTheme } from '../contexts/ThemeContext';
import api from '../services/api';

const legislationTypes = [
  'All Types',
  'Act',
  'Ordinance',
  'Subordinate Statutory Notification / SRO',
  'Rules',
  'Regulations',
  'Presidential Order / Statutory Order',
  'Code / Manual',
  'Notification / SRO'
];

const jurisdictions = [
  'All Jurisdictions',
  'Federal',
  'Federal / Statutory Body',
  'Punjab',
  'Sindh',
  'KPK',
  'Balochistan'
];

const issuingBodies = [
  'All Issuing Authorities',
  'Federal Board of Revenue (FBR)',
  'Securities & Exchange Commission of Pakistan (SECP)',
  'State Bank of Pakistan (SBP)',
  'Ministry of Finance',
  'Ministry of Law & Justice',
  'NEPRA / OGRA / Regulatory Authority',
  'Provincial Board of Revenue',
  'Parliament of Pakistan'
];

export const getStatuteBifurcationBadge = (st: any) => {
  const typeStr = (st.legislation_type || st.type || '').toLowerCase();
  const titleStr = (st.title || st.name || '').toLowerCase();
  const notifStr = (st.notification_number || st.sro_number || '').toLowerCase();

  if (typeStr.includes('sro') || notifStr.includes('sro') || titleStr.includes('s.r.o') || titleStr.includes('sro ')) {
    return {
      type: 'sro',
      label: '📑 S.R.O. REGULATION',
      bg: 'linear-gradient(135deg, #4c1d95 0%, #312e81 100%)',
      color: '#ddd6fe',
      border: '#a78bfa'
    };
  }
  if (typeStr.includes('notification') || titleStr.includes('notification') || titleStr.includes('circular') || notifStr.includes('notif')) {
    return {
      type: 'notification',
      label: '📢 GAZETTE NOTIFICATION',
      bg: 'linear-gradient(135deg, #0e7490 0%, #155e75 100%)',
      color: '#cffafe',
      border: '#22d3ee'
    };
  }
  if (typeStr.includes('act') || titleStr.includes(' act') || titleStr.startsWith('act ')) {
    return {
      type: 'act',
      label: '📜 LEGISLATIVE ACT',
      bg: 'linear-gradient(135deg, #065f46 0%, #064e3b 100%)',
      color: '#a7f3d0',
      border: '#34d399'
    };
  }
  if (typeStr.includes('ordinance') || titleStr.includes('ordinance')) {
    return {
      type: 'ordinance',
      label: '⚖️ STATUTORY ORDINANCE',
      bg: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
      color: '#bfdbfe',
      border: '#60a5fa'
    };
  }
  if (typeStr.includes('rule') || typeStr.includes('regulation') || titleStr.includes(' rules') || titleStr.includes('regulations')) {
    return {
      type: 'rules',
      label: '⚙️ SUBORDINATE RULES',
      bg: 'linear-gradient(135deg, #854d0e 0%, #713f12 100%)',
      color: '#fef08a',
      border: '#facc15'
    };
  }
  return {
    type: 'act',
    label: '📜 STATUTORY ENACTMENT',
    bg: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    color: '#ecc94b',
    border: '#ecc94b'
  };
};

const ALPHABET_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'Z'];

export const StatutesDirectory: React.FC = () => {
  const location = useLocation();
  const [query, setQuery] = useState('');
  const [selectedAlphabet, setSelectedAlphabet] = useState<string>('ALL');
  const [selectedType, setSelectedType] = useState('All Types');
  const [selectedJurisdiction, setSelectedJurisdiction] = useState('All Jurisdictions');
  const [selectedIssuingBody, setSelectedIssuingBody] = useState('All Issuing Authorities');
  const [sroNumber, setSroNumber] = useState('');
  const [startYear, setStartYear] = useState('');
  const [endYear, setEndYear] = useState('');
  const [sortBy, setSortBy] = useState('alphabetical');
  const [currentPage, setCurrentPage] = useState(1);
  
  const [statuteBifurcationTab, setStatuteBifurcationTab] = useState<'all' | 'act' | 'sro' | 'notification' | 'ordinance' | 'rules' | 'dictionary'>('all');
  const [statutesList, setStatutesList] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingStatutes, setLoadingStatutes] = useState(true);

  const [statsData, setStatsData] = useState<any>({
    totalStatutes: 10768,
    totalSections: 86267,
    totalWords: 34210660,
    alphabetCounts: {
      A: 614, B: 708, C: 941, D: 219, E: 297, F: 396, G: 171, H: 154,
      I: 386, J: 28, K: 424, L: 222, M: 409, N: 773, O: 102, P: 2354,
      Q: 25, R: 294, S: 1206, T: 295, U: 138, V: 17, W: 565, Z: 30
    }
  });

  useEffect(() => {
    api.get('/statutes/stats').then(res => {
      if (res.data && res.data.totalStatutes) {
        setStatsData(res.data);
      }
    }).catch(() => {});
  }, []);
  
  // View Mode: 'grid' (Default) vs 'table' vs 'list'
  const [viewMode, setViewMode] = useState<'grid' | 'table' | 'list'>('grid');
  const resultsContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (typeof window !== 'undefined') {
        setShowScrollTop(window.scrollY > 300);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleScrollDown = () => {
    if (resultsContainerRef.current) {
      resultsContainerRef.current.scrollIntoView({ behavior: 'smooth' });
    } else if (typeof window !== 'undefined') {
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    }
  };

  const handleScrollToTop = () => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Selected document for modal & Dictionary Modal state & Instant QR Share Modal state
  const [activeModalDoc, setActiveModalDoc] = useState<any | null>(null);
  const [modalViewMode, setModalViewMode] = useState<'brief' | 'detailed' | 'sections'>('brief');
  const [isDictOpen, setIsDictOpen] = useState<boolean>(false);
  const [qrModalDoc, setQrModalDoc] = useState<any | null>(null);
  const [copiedQrLink, setCopiedQrLink] = useState<boolean>(false);

  // Dynamic tab counts calculation
  const statuteCounts = React.useMemo(() => {
    const total = totalCount > 0 ? totalCount : 10768;
    const isFiltered = Boolean(query || sroNumber || selectedAlphabet !== 'ALL' || selectedJurisdiction !== 'All Jurisdictions' || selectedIssuingBody !== 'All Issuing Authorities' || startYear || endYear);
    
    if (!isFiltered && selectedType === 'All Types') {
      return {
        all: statsData.totalStatutes || 10768,
        act: 6120,
        sro: 3150,
        notification: 885,
        ordinance: 613,
        rules: 360,
        dictionary: 8660
      };
    }
    
    const actEst = Math.max(1, Math.round(total * 0.568));
    const sroEst = Math.max(1, Math.round(total * 0.292));
    const notifEst = Math.max(1, Math.round(total * 0.082));
    const ordEst = Math.max(1, Math.round(total * 0.057));
    const rulesEst = Math.max(1, Math.round(total * 0.033));

    return {
      all: total,
      act: actEst,
      sro: sroEst,
      notification: notifEst,
      ordinance: ordEst,
      rules: rulesEst,
      dictionary: 8660
    };
  }, [totalCount, query, sroNumber, selectedAlphabet, selectedJurisdiction, selectedIssuingBody, startYear, endYear, selectedType, statsData]);

  // Current Active Tab Totals and Labels for Panes
  const currentStatuteTabTotalCount = React.useMemo(() => {
    if (statuteBifurcationTab === 'act') return statuteCounts.act;
    if (statuteBifurcationTab === 'sro') return statuteCounts.sro;
    if (statuteBifurcationTab === 'notification') return statuteCounts.notification;
    if (statuteBifurcationTab === 'ordinance') return statuteCounts.ordinance;
    if (statuteBifurcationTab === 'rules') return statuteCounts.rules;
    if (statuteBifurcationTab === 'dictionary') return statuteCounts.dictionary;
    return statuteCounts.all;
  }, [statuteBifurcationTab, statuteCounts]);

  const currentStatuteTabLabel = React.useMemo(() => {
    if (statuteBifurcationTab === 'act') return 'Legislative Acts';
    if (statuteBifurcationTab === 'sro') return 'SROs & Regulatory Orders';
    if (statuteBifurcationTab === 'notification') return 'Gazette Notifications';
    if (statuteBifurcationTab === 'ordinance') return 'Statutory Ordinances';
    if (statuteBifurcationTab === 'rules') return 'Rules & Codes';
    if (statuteBifurcationTab === 'dictionary') return 'Law Dictionary';
    return 'All Statutory Records';
  }, [statuteBifurcationTab]);

  useEffect(() => {
    if (location.search.includes('tab=dictionary')) {
      setIsDictOpen(true);
    }
    const searchParams = new URLSearchParams(location.search);
    const idParam = searchParams.get('id') || searchParams.get('statuteId') || searchParams.get('docId');
    if (idParam) {
      api.get(`/statutes/${encodeURIComponent(idParam)}`).then(res => {
        if (res.data) setActiveModalDoc(res.data);
      }).catch(e => {
        console.warn('Statute fetch error:', e);
      });
    }
  }, [location.search]);

  // Dynamic API Fetch with fallback
  const fetchStatutes = useCallback(async () => {
    setLoadingStatutes(true);
    try {
      const params = new URLSearchParams();
      if (query.trim()) params.append('q', query.trim());
      if (selectedAlphabet && selectedAlphabet !== 'ALL') params.append('alphabet', selectedAlphabet);
      if (sroNumber.trim()) params.append('sro', sroNumber.trim());
      if (selectedType !== 'All Types') params.append('type', selectedType);
      if (selectedJurisdiction !== 'All Jurisdictions') params.append('jurisdiction', selectedJurisdiction);
      if (selectedIssuingBody !== 'All Issuing Authorities') params.append('issuingBody', selectedIssuingBody.split(' (')[0]);
      if (startYear.trim()) params.append('startYear', startYear.trim());
      if (endYear.trim()) params.append('endYear', endYear.trim());
      params.append('sortBy', sortBy);
      params.append('page', currentPage.toString());
      params.append('limit', '30');

      const response = await api.get(`/statutes?${params.toString()}`);
      if (response.data && response.data.statutes) {
        setStatutesList(response.data.statutes);
        setTotalCount(response.data.total || response.data.statutes.length);
        setTotalPages(response.data.totalPages || 1);
        setLoadingStatutes(false);
        return;
      }
    } catch (err) {
      console.warn('Backend API search fallback to local matrix:', err);
    }

    // Local JSON Fallback if offline
    try {
      const res = await fetch('/elum_statutes_matrix_full.json');
      if (res.ok) {
        const data = await res.json();
        const raw = Array.isArray(data) ? data : (data.statutes || []);
        
        let filtered = raw.filter((item: any) => {
          const title = (item.title || item.name || '').toLowerCase();
          const itemAlpha = (item.alphabet || (item.title ? item.title.charAt(0) : 'A')).toUpperCase();
          const type = (item.legislation_type || item.type || '').toLowerCase();
          const juris = (item.jurisdiction || item.province || '').toLowerCase();
          const notif = (item.notification_number || item.sro_number || '').toLowerCase();
          const desc = (item.short_description || item.detailed_theme || '').toLowerCase();
          const yr = (item.year || '').toString();

          if (selectedAlphabet !== 'ALL' && itemAlpha !== selectedAlphabet) return false;
          if (query.trim() && !title.includes(query.toLowerCase()) && !desc.includes(query.toLowerCase()) && !notif.includes(query.toLowerCase())) return false;
          if (sroNumber.trim() && !notif.includes(sroNumber.toLowerCase().trim())) return false;
          if (selectedType !== 'All Types' && !type.includes(selectedType.toLowerCase())) return false;
          if (selectedJurisdiction !== 'All Jurisdictions' && !juris.includes(selectedJurisdiction.toLowerCase())) return false;
          if (startYear.trim() && parseInt(yr, 10) < parseInt(startYear.trim(), 10)) return false;
          if (endYear.trim() && parseInt(yr, 10) > parseInt(endYear.trim(), 10)) return false;
          return true;
        });

        // Strict Local Title Deduplication
        const localSeenTitles = new Set<string>();
        const uniqueFiltered: any[] = [];
        for (const item of filtered) {
          const t = (item.title || item.name || '').toLowerCase().trim();
          if (t && !localSeenTitles.has(t)) {
            localSeenTitles.add(t);
            uniqueFiltered.push(item);
          }
        }

        setTotalCount(uniqueFiltered.length);
        setTotalPages(Math.ceil(uniqueFiltered.length / 30) || 1);
        const start = (currentPage - 1) * 30;
        setStatutesList(uniqueFiltered.slice(start, start + 30));
      }
    } catch (fallbackErr) {
      console.error('Error fetching fallback statutes:', fallbackErr);
    } finally {
      setLoadingStatutes(false);
    }
  }, [query, selectedAlphabet, sroNumber, selectedType, selectedJurisdiction, selectedIssuingBody, startYear, endYear, sortBy, currentPage]);

  useEffect(() => {
    fetchStatutes();
  }, [fetchStatutes]);

  const handleReset = () => {
    setQuery('');
    setSelectedAlphabet('ALL');
    setSelectedType('All Types');
    setSelectedJurisdiction('All Jurisdictions');
    setSelectedIssuingBody('All Issuing Authorities');
    setSroNumber('');
    setStartYear('');
    setEndYear('');
    setSortBy('alphabetical');
    setCurrentPage(1);
  };

  const { colors, themeMode, setThemeMode } = useTheme();

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.pageBg, fontFamily: "'Inter', sans-serif", color: colors.textColor, transition: 'all 0.3s ease' }}>
      
      {/* HEADER NAVIGATION BAR */}
      <header style={{ backgroundColor: colors.headerBg, borderBottom: `3px solid ${colors.goldPrimary}`, color: 'white', padding: '1rem 2rem' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
          
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', textDecoration: 'none', color: 'white' }}>
            <img 
              src="/logo_Elum_Firm.png" 
              alt="ELum Law Logo" 
              style={{ height: '45px', width: '45px', borderRadius: '8px', border: `2px solid ${colors.goldPrimary}`, objectFit: 'cover' }} 
              onError={(e: any) => { e.target.src = '/logo_ELum_Firm4.png'; }}
            />
            <div>
              <div style={{ fontSize: '1.35rem', fontWeight: '900', color: colors.goldPrimary, letterSpacing: '0.5px' }}>
                ELum Law Associates
              </div>
              <div style={{ fontSize: '0.75rem', color: '#cbd5e1', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px' }}>
                Statutes, Acts, Ordinances & Statutory SROs Engine
              </div>
            </div>
          </Link>

          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            {/* 3-WAY THEME CONTROLLER */}
            <div style={{ display: 'flex', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.1)', padding: '0.2rem', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)' }}>
              <button 
                onClick={() => setThemeMode('dark')}
                style={{ backgroundColor: themeMode === 'dark' ? colors.goldPrimary : 'transparent', color: themeMode === 'dark' ? '#0f172a' : '#cbd5e1', border: 'none', borderRadius: '6px', padding: '0.35rem 0.65rem', fontSize: '0.78rem', fontWeight: '800', cursor: 'pointer' }}
              >
                🌙 Dark
              </button>
              <button 
                onClick={() => setThemeMode('light')}
                style={{ backgroundColor: themeMode === 'light' ? colors.goldPrimary : 'transparent', color: themeMode === 'light' ? '#0f172a' : '#cbd5e1', border: 'none', borderRadius: '6px', padding: '0.35rem 0.65rem', fontSize: '0.78rem', fontWeight: '800', cursor: 'pointer' }}
              >
                ☀️ Light
              </button>
              <button 
                onClick={() => setThemeMode('easy')}
                style={{ backgroundColor: themeMode === 'easy' ? '#10b981' : 'transparent', color: themeMode === 'easy' ? '#ffffff' : '#cbd5e1', border: 'none', borderRadius: '6px', padding: '0.35rem 0.65rem', fontSize: '0.78rem', fontWeight: '800', cursor: 'pointer' }}
              >
                👁️ Eye-Comfort
              </button>
            </div>

            <Link to="/cases/search" style={{ color: '#38bdf8', textDecoration: 'none', fontWeight: '700', fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: '0.4rem', backgroundColor: 'rgba(56,189,248,0.1)', padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid rgba(56,189,248,0.3)' }}>
              <BookOpen size={16} /> Case Laws Directory
            </Link>
            <Link to="/" style={{ color: colors.goldPrimary, textDecoration: 'none', fontWeight: '800', fontSize: '0.88rem' }}>
              Home Portal
            </Link>
          </div>

        </div>
      </header>

      {/* MAIN CONTAINER */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '2rem 1.5rem' }}>
        
        {/* ADVANCED SEARCH PANEL */}
        <div style={{ backgroundColor: 'white', borderRadius: '16px', border: '1px solid #cbd5e1', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', padding: '1.75rem', marginBottom: '2rem' }}>
          
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <Link to="/statutes" style={{ textDecoration: 'none' }}>
                <h1 style={{ fontSize: '1.5rem', fontWeight: '800', color: '#0f172a', margin: 0, display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                  <Sparkles size={28} color="#0284c7" /> Intelligent Acts, Laws & Statutory SROs Search Engine
                </h1>
              </Link>
              <p style={{ margin: '0.3rem 0 0 0', fontSize: '0.88rem', color: '#64748b' }}>
                Multi-field keyword tokenization & relevance search over 150,000+ Acts, Ordinances, Statutory Rules, SROs & Notifications.
              </p>

              {/* CROSS-LINKED BADGES FOR CASE LAWS, AI ASSISTANT, & DICTIONARY */}
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.6rem' }}>
                <Link to="/law-directory" style={{ textDecoration: 'none' }}>
                  <span style={{ backgroundColor: '#0284c7', color: 'white', fontSize: '0.75rem', fontWeight: '800', padding: '0.3rem 0.75rem', borderRadius: '20px', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', cursor: 'pointer' }}>
                    ⚖️ Case Law & Citation Search Engine
                  </span>
                </Link>
                <Link to="/ai-assistant" style={{ textDecoration: 'none' }}>
                  <span style={{ backgroundColor: '#0f172a', color: '#ecc94b', fontSize: '0.75rem', fontWeight: '800', padding: '0.3rem 0.75rem', borderRadius: '20px', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', border: '1px solid #ecc94b', cursor: 'pointer' }}>
                    🤖 AI Assistant & Legal Drafter
                  </span>
                </Link>
                <button
                  onClick={() => setIsDictOpen(true)}
                  style={{ backgroundColor: '#fef3c7', color: '#92400e', fontSize: '0.75rem', fontWeight: '800', padding: '0.3rem 0.75rem', borderRadius: '20px', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', border: '1px solid #fde68a', cursor: 'pointer' }}
                >
                  📖 Open Elum Law Dictionary (8,660 Terms)
                </button>
              </div>
            </div>
            <span style={{ fontSize: '0.85rem', backgroundColor: '#e0f2fe', color: '#0369a1', padding: '0.45rem 1rem', borderRadius: '20px', fontWeight: '800', border: '1px solid #bae6fd' }}>
              ⚡ High-Speed Relevance Engine
            </span>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); setCurrentPage(1); fetchStatutes(); }} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            
            {/* ROW 1: KEYWORD & SRO NUMBER */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem' }}>
              
              {/* Keyword / Statute Title */}
              <div>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '700', color: '#475569', marginBottom: '0.45rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Statute Title / Law Keyword
                </label>
                <div style={{ position: 'relative' }}>
                  <Search size={18} color="#94a3b8" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }} />
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); setCurrentPage(1); }}
                    placeholder="e.g. Income Tax, Penal Code, Local Government, Sales Tax..."
                    style={{ width: '100%', height: '46px', padding: '0 0.75rem 0 2.6rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              {/* SRO / Notification Number */}
              <div>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '700', color: '#0284c7', marginBottom: '0.45rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  🔖 SRO / Notification Number Pinpoint
                </label>
                <input
                  type="text"
                  value={sroNumber}
                  onChange={(e) => { setSroNumber(e.target.value); setCurrentPage(1); }}
                  placeholder="e.g. 111(I)/2019, SRO 46(I)/2019, Notif 401..."
                  style={{ width: '100%', height: '46px', padding: '0 0.85rem', borderRadius: '8px', border: '2px solid #38bdf8', fontSize: '0.95rem', backgroundColor: '#f0f9ff', fontWeight: '600', boxSizing: 'border-box' }}
                />
              </div>

            </div>

            {/* ROW 2: LEGISLATION TYPE, JURISDICTION & ISSUING AUTHORITY */}
            <div style={{ backgroundColor: '#f8fafc', padding: '1.25rem', borderRadius: '12px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#475569', fontSize: '0.85rem', fontWeight: '700', textTransform: 'uppercase' }}>
                <Filter size={16} color="#0284c7" /> Advanced Filters (Jurisdiction, Type & Issuing Authority)
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.25rem' }}>
                
                {/* Legislation Type */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '700', color: '#64748b', marginBottom: '0.4rem', textTransform: 'uppercase' }}>
                    Legislation Type
                  </label>
                  <select
                    value={selectedType}
                    onChange={(e) => { setSelectedType(e.target.value); setCurrentPage(1); }}
                    style={{ width: '100%', height: '44px', padding: '0 0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', backgroundColor: 'white' }}
                  >
                    {legislationTypes.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>

                {/* Jurisdiction */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '700', color: '#64748b', marginBottom: '0.4rem', textTransform: 'uppercase' }}>
                    Jurisdiction / Province
                  </label>
                  <select
                    value={selectedJurisdiction}
                    onChange={(e) => { setSelectedJurisdiction(e.target.value); setCurrentPage(1); }}
                    style={{ width: '100%', height: '44px', padding: '0 0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', backgroundColor: 'white' }}
                  >
                    {jurisdictions.map(j => <option key={j} value={j}>{j}</option>)}
                  </select>
                </div>

                {/* Issuing Authority / Body */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '700', color: '#0369a1', marginBottom: '0.4rem', textTransform: 'uppercase' }}>
                    🏢 Issuing Authority / Institution
                  </label>
                  <select
                    value={selectedIssuingBody}
                    onChange={(e) => { setSelectedIssuingBody(e.target.value); setCurrentPage(1); }}
                    style={{ width: '100%', height: '44px', padding: '0 0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', backgroundColor: 'white' }}
                  >
                    {issuingBodies.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>

                {/* Sort By */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '700', color: '#64748b', marginBottom: '0.4rem', textTransform: 'uppercase' }}>
                    <ArrowUpDown size={12} /> Sort Order
                  </label>
                  <select
                    value={sortBy}
                    onChange={(e) => { setSortBy(e.target.value); setCurrentPage(1); }}
                    style={{ width: '100%', height: '44px', padding: '0 0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', backgroundColor: 'white', fontWeight: '700' }}
                  >
                    <option value="relevance">🎯 Highest Relevance Score</option>
                    <option value="newest">📅 Newest Enactment First</option>
                    <option value="oldest">📜 Oldest Enactment First</option>
                    <option value="alphabetical">🔤 Title Alphabetical (A-Z)</option>
                  </select>
                </div>

              </div>

            </div>

            {/* BUTTON ACTIONS */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={handleReset}
                style={{ padding: '0.7rem 1.4rem', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: 'white', color: '#64748b', cursor: 'pointer', fontWeight: '700', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.45rem' }}
              >
                <RotateCcw size={16} /> Reset Filters
              </button>
              <button
                type="submit"
                style={{ padding: '0.7rem 1.75rem', borderRadius: '8px', border: 'none', backgroundColor: '#0284c7', color: 'white', cursor: 'pointer', fontWeight: '800', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.55rem', boxShadow: '0 4px 12px rgba(2, 132, 199, 0.35)' }}
              >
                <Search size={18} /> Search Acts, Laws & SROs
              </button>
            </div>

          </form>

        </div>

        {/* STATUTES BIFURCATED TABS BAR: Dynamic Counts on All Tabs */}
        <div style={{ 
          backgroundColor: 'white', 
          borderRadius: '14px', 
          border: '1px solid #cbd5e1', 
          padding: '0.85rem 1.25rem', 
          marginBottom: '1.5rem', 
          display: 'flex', 
          gap: '0.6rem', 
          flexWrap: 'wrap', 
          alignItems: 'center',
          boxShadow: '0 2px 4px rgba(0,0,0,0.03)'
        }}>
          <span style={{ fontSize: '0.85rem', fontWeight: '800', color: '#0f172a', textTransform: 'uppercase', marginRight: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <Layers size={18} color="#0284c7" /> Statutory Bifurcation:
          </span>

          <button
            onClick={() => {
              setStatuteBifurcationTab('all');
              setSelectedType('All Types');
            }}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              border: statuteBifurcationTab === 'all' ? '2px solid #0284c7' : '1px solid #e2e8f0',
              backgroundColor: statuteBifurcationTab === 'all' ? '#0284c7' : '#f8fafc',
              color: statuteBifurcationTab === 'all' ? 'white' : '#334155',
              fontWeight: '800',
              fontSize: '0.85rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              transition: 'all 0.15s ease-in-out'
            }}
          >
            🏛️ All Records ({statuteCounts.all.toLocaleString()})
          </button>

          <button
            onClick={() => {
              setStatuteBifurcationTab('act');
              setSelectedType('Act');
            }}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              border: statuteBifurcationTab === 'act' ? '2px solid #059669' : '1px solid #a7f3d0',
              backgroundColor: statuteBifurcationTab === 'act' ? '#059669' : '#ecfdf5',
              color: statuteBifurcationTab === 'act' ? 'white' : '#065f46',
              fontWeight: '800',
              fontSize: '0.85rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              transition: 'all 0.15s ease-in-out'
            }}
          >
            📜 Legislative Acts ({statuteCounts.act.toLocaleString()})
          </button>

          <button
            onClick={() => {
              setStatuteBifurcationTab('sro');
              setSelectedType('Subordinate Statutory Notification / SRO');
            }}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              border: statuteBifurcationTab === 'sro' ? '2px solid #7c3aed' : '1px solid #ddd6fe',
              backgroundColor: statuteBifurcationTab === 'sro' ? '#7c3aed' : '#f5f3ff',
              color: statuteBifurcationTab === 'sro' ? 'white' : '#5b21b6',
              fontWeight: '800',
              fontSize: '0.85rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              transition: 'all 0.15s ease-in-out'
            }}
          >
            📑 SROs & Regulations ({statuteCounts.sro.toLocaleString()})
          </button>

          <button
            onClick={() => {
              setStatuteBifurcationTab('notification');
              setSelectedType('Notification / SRO');
            }}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              border: statuteBifurcationTab === 'notification' ? '2px solid #0891b2' : '1px solid #a5f3fc',
              backgroundColor: statuteBifurcationTab === 'notification' ? '#0891b2' : '#ecfeff',
              color: statuteBifurcationTab === 'notification' ? 'white' : '#155e75',
              fontWeight: '800',
              fontSize: '0.85rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              transition: 'all 0.15s ease-in-out'
            }}
          >
            📢 Gazette Notifications ({statuteCounts.notification.toLocaleString()})
          </button>

          <button
            onClick={() => {
              setStatuteBifurcationTab('ordinance');
              setSelectedType('Ordinance');
            }}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              border: statuteBifurcationTab === 'ordinance' ? '2px solid #1e3a8a' : '1px solid #bfdbfe',
              backgroundColor: statuteBifurcationTab === 'ordinance' ? '#1e3a8a' : '#eff6ff',
              color: statuteBifurcationTab === 'ordinance' ? 'white' : '#1e40af',
              fontWeight: '800',
              fontSize: '0.85rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              transition: 'all 0.15s ease-in-out'
            }}
          >
            ⚖️ Ordinances ({statuteCounts.ordinance.toLocaleString()})
          </button>

          <button
            onClick={() => {
              setStatuteBifurcationTab('rules');
              setSelectedType('Rules');
            }}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              border: statuteBifurcationTab === 'rules' ? '2px solid #d97706' : '1px solid #fde68a',
              backgroundColor: statuteBifurcationTab === 'rules' ? '#d97706' : '#fffbeb',
              color: statuteBifurcationTab === 'rules' ? 'white' : '#92400e',
              fontWeight: '800',
              fontSize: '0.85rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              transition: 'all 0.15s ease-in-out'
            }}
          >
            ⚙️ Rules & Codes ({statuteCounts.rules.toLocaleString()})
          </button>

          <button
            onClick={() => setIsDictOpen(true)}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              border: '1px solid #fde68a',
              backgroundColor: '#fef3c7',
              color: '#92400e',
              fontWeight: '800',
              fontSize: '0.85rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              marginLeft: 'auto'
            }}
          >
            📖 Law Dictionary ({statuteCounts.dictionary.toLocaleString()} Terms)
          </button>
        </div>

        {/* ALPHABETICAL STATUTES & ACTS INDEX (A TO Z) */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '14px',
          border: '1px solid #cbd5e1',
          padding: '0.9rem 1.25rem',
          marginBottom: '1.5rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.65rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.03)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: '800', color: '#0f172a', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              🔤 Alphabetical Statute & Act Index:
            </span>
            <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: '600' }}>
              Direct alphabetical access across <strong>{(statsData.totalStatutes || 10768).toLocaleString()}</strong> Acts & <strong>{(statsData.totalSections || 86267).toLocaleString()}</strong> sections
            </span>
          </div>

          <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              onClick={() => { setSelectedAlphabet('ALL'); setCurrentPage(1); }}
              style={{
                padding: '0.4rem 0.8rem',
                borderRadius: '6px',
                border: selectedAlphabet === 'ALL' ? '2px solid #0f172a' : '1px solid #cbd5e1',
                backgroundColor: selectedAlphabet === 'ALL' ? '#0f172a' : '#f8fafc',
                color: selectedAlphabet === 'ALL' ? '#ecc94b' : '#334155',
                fontWeight: '800',
                fontSize: '0.8rem',
                cursor: 'pointer'
              }}
            >
              ALL ({(statsData.totalStatutes || 10768).toLocaleString()})
            </button>
            {ALPHABET_LETTERS.map((letter) => {
              const cnt = statsData.alphabetCounts?.[letter] || 0;
              const isSelected = selectedAlphabet === letter;
              return (
                <button
                  key={letter}
                  onClick={() => { setSelectedAlphabet(letter); setCurrentPage(1); }}
                  style={{
                    padding: '0.35rem 0.55rem',
                    borderRadius: '6px',
                    border: isSelected ? '2px solid #0284c7' : '1px solid #e2e8f0',
                    backgroundColor: isSelected ? '#0284c7' : (cnt > 0 ? 'white' : '#f1f5f9'),
                    color: isSelected ? 'white' : (cnt > 0 ? '#0f172a' : '#94a3b8'),
                    fontWeight: isSelected ? '800' : '600',
                    fontSize: '0.78rem',
                    cursor: cnt > 0 ? 'pointer' : 'default',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem'
                  }}
                  disabled={cnt === 0}
                >
                  <span>{letter}</span>
                  {cnt > 0 && (
                    <span style={{
                      fontSize: '0.65rem',
                      backgroundColor: isSelected ? 'rgba(255,255,255,0.25)' : '#f1f5f9',
                      color: isSelected ? 'white' : '#64748b',
                      padding: '1px 4px',
                      borderRadius: '4px'
                    }}>
                      {cnt}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* RESULTS HEADER & VIEW CONTROLS & SCROLL ACTIONS */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem', backgroundColor: '#e0f2fe', padding: '1rem 1.25rem', borderRadius: '12px', borderLeft: '6px solid #0284c7', flexWrap: 'wrap', gap: '1rem', boxShadow: '0 2px 6px rgba(2, 132, 199, 0.08)' }}>
          <div>
            <div style={{ fontSize: '1.15rem', fontWeight: '800', color: '#0369a1', display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0 0 0.25rem 0' }}>
              <Layers size={22} color="#0284c7" />
              {currentStatuteTabLabel}: <span style={{ color: '#0f172a' }}>{currentStatuteTabTotalCount.toLocaleString()} Total Records</span>
            </div>
            <div style={{ fontSize: '0.85rem', color: '#0369a1', fontWeight: '600' }}>
              Page {currentPage} of {Math.max(1, Math.ceil(currentStatuteTabTotalCount / 30)).toLocaleString()} (Showing {statutesList.length} items in {viewMode.toUpperCase()} Form)
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', flexWrap: 'wrap' }}>
            <span style={{ backgroundColor: '#0284c7', color: 'white', padding: '0.35rem 0.85rem', borderRadius: '20px', fontSize: '0.85rem', fontWeight: '800', boxShadow: '0 2px 4px rgba(2, 132, 199, 0.2)' }}>
              📊 Total Count: {currentStatuteTabTotalCount.toLocaleString()}
            </span>

            <button
              onClick={handleScrollDown}
              title="Click to scroll down directly to results grid / table and pagination"
              style={{
                backgroundColor: '#ffffff',
                color: '#0284c7',
                border: '1px solid #bae6fd',
                borderRadius: '8px',
                padding: '0.4rem 0.85rem',
                fontSize: '0.8rem',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.35rem'
              }}
            >
              <ArrowDown size={15} /> Scroll Down
            </button>

            <div style={{ display: 'flex', backgroundColor: '#ffffff', borderRadius: '8px', padding: '0.2rem', border: '1px solid #bae6fd' }}>
              <button
                onClick={() => setViewMode('grid')}
                title="Grid Form View (Multi-Column Cards)"
                style={{
                  backgroundColor: viewMode === 'grid' ? '#0284c7' : 'transparent',
                  color: viewMode === 'grid' ? 'white' : '#64748b',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.35rem 0.65rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.3rem',
                  fontSize: '0.8rem',
                  fontWeight: '700'
                }}
              >
                <LayoutGrid size={15} /> Grid Form
              </button>
              <button
                onClick={() => setViewMode('table')}
                title="Table Form View (Structured Data Table)"
                style={{
                  backgroundColor: viewMode === 'table' ? '#0284c7' : 'transparent',
                  color: viewMode === 'table' ? 'white' : '#64748b',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.35rem 0.65rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.3rem',
                  fontSize: '0.8rem',
                  fontWeight: '700'
                }}
              >
                <Table size={15} /> Table Form
              </button>
              <button
                onClick={() => setViewMode('list')}
                title="List Form View (Full Width Cards)"
                style={{
                  backgroundColor: viewMode === 'list' ? '#0284c7' : 'transparent',
                  color: viewMode === 'list' ? 'white' : '#64748b',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.35rem 0.65rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.3rem',
                  fontSize: '0.8rem',
                  fontWeight: '700'
                }}
              >
                <List size={15} /> List Form
              </button>
            </div>
          </div>
        </div>

        {/* LOADING INDICATOR */}
        {loadingStatutes ? (
          <div style={{ textAlign: 'center', padding: '4rem 0', backgroundColor: 'white', borderRadius: '16px', border: '1px solid #cbd5e1' }}>
            <Sparkles size={36} color="#0284c7" style={{ animation: 'spin 2s linear infinite' }} />
            <div style={{ marginTop: '1rem', fontSize: '1.1rem', fontWeight: '800', color: '#0f172a' }}>
              Searching Acts, Laws & Statutory SROs Repository...
            </div>
          </div>
        ) : statutesList.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '4rem 0', backgroundColor: 'white', borderRadius: '16px', border: '1px solid #cbd5e1' }}>
            <AlertTriangle size={36} color="#dc2626" />
            <div style={{ marginTop: '1rem', fontSize: '1.1rem', fontWeight: '800', color: '#0f172a' }}>
              No Acts, Laws, or SROs matched your query.
            </div>
            <p style={{ color: '#64748b', fontSize: '0.9rem', margin: '0.5rem 0 1rem 0' }}>
              Try broadening your keyword, resetting filters, or searching by partial SRO notification number.
            </p>
            <button
              onClick={handleReset}
              style={{ padding: '0.6rem 1.25rem', borderRadius: '8px', border: 'none', backgroundColor: '#0284c7', color: 'white', fontWeight: '700', cursor: 'pointer' }}
            >
              Reset Search Filters
            </button>
          </div>
        ) : viewMode === 'table' ? (
          /* STATUTES TABLE FORM VIEW */
          <div ref={resultsContainerRef} style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflowX: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', maxHeight: '72vh', overflowY: 'auto', marginBottom: '2rem' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ backgroundColor: '#0f172a', color: 'white', borderBottom: '2px solid #0284c7' }}>
                  <th style={{ padding: '0.85rem 1rem', width: '60px', textAlign: 'center' }}>S#</th>
                  <th style={{ padding: '0.85rem 1rem', width: '170px' }}>Classification</th>
                  <th style={{ padding: '0.85rem 1rem' }}>Statute / Enactment Title</th>
                  <th style={{ padding: '0.85rem 1rem', width: '220px' }}>Jurisdiction, Year & Institution</th>
                  <th style={{ padding: '0.85rem 1rem', width: '280px' }}>Brief Outline</th>
                  <th style={{ padding: '0.85rem 1rem', width: '170px', textAlign: 'center' }}>Actions & QR</th>
                </tr>
              </thead>
              <tbody>
                {statutesList.map((st: any, idx: number) => {
                  const stTitle = st.title || st.name || 'Statutory Record';
                  const stJuris = st.jurisdiction || st.province || 'Federal';
                  const stNotif = st.notification_number || st.sro_number || '';
                  const stInst = st.issuing_institution || '';
                  const stDesc = st.short_description || st.detailed_theme || 'Statutory Enactment indexed under ELum Law Repository archives.';
                  const badgeInfo = getStatuteBifurcationBadge(st);
                  const serialNum = (currentPage - 1) * 30 + (idx + 1);

                  return (
                    <tr key={st.id || idx} style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                      <td style={{ padding: '0.85rem 1rem', textAlign: 'center', fontWeight: '800', color: '#0f172a' }}>
                        <span style={{ backgroundColor: '#0f172a', color: 'white', padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem' }}>
                          {serialNum}
                        </span>
                      </td>
                      <td style={{ padding: '0.85rem 1rem' }}>
                        <span style={{ background: badgeInfo.bg, color: badgeInfo.color, border: `1px solid ${badgeInfo.border}`, padding: '0.25rem 0.55rem', borderRadius: '6px', fontSize: '0.7rem', fontWeight: '800', display: 'inline-block' }}>
                          {badgeInfo.label}
                        </span>
                      </td>
                      <td style={{ padding: '0.85rem 1rem' }}>
                        <button 
                          onClick={() => { setActiveModalDoc(st); setModalViewMode('brief'); }} 
                          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', color: '#0284c7', fontWeight: '800', fontSize: '0.95rem', textDecoration: 'underline' }}
                        >
                          {stTitle}
                        </button>
                      </td>
                      <td style={{ padding: '0.85rem 1rem', fontSize: '0.8rem', color: '#475569' }}>
                        <div><strong>🏛️ {stJuris}</strong> {st.year && `(${st.year})`}</div>
                        {stNotif && <div>🔖 SRO/Notif: {stNotif}</div>}
                        {stInst && <div style={{ color: '#15803d', fontWeight: '700' }}>🏢 {stInst}</div>}
                      </td>
                      <td style={{ padding: '0.85rem 1rem', fontSize: '0.82rem', color: '#334155', lineHeight: '1.4' }}>
                        {stDesc.substring(0, 140)}...
                      </td>
                      <td style={{ padding: '0.85rem 1rem', textAlign: 'center' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', alignItems: 'center' }}>
                          <button 
                            onClick={() => { setActiveModalDoc(st); setModalViewMode('brief'); }} 
                            style={{ width: '100%', backgroundColor: '#0284c7', color: 'white', border: 'none', padding: '0.3rem 0.6rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.3rem' }}
                          >
                            <FileText size={12} /> Brief
                          </button>
                          <button 
                            onClick={() => setQrModalDoc(st)} 
                            style={{ width: '100%', backgroundColor: '#ffffff', color: '#0284c7', border: '1px solid #bae6fd', padding: '0.3rem 0.6rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.3rem' }}
                          >
                            <QrCode size={12} /> QR Share
                          </button>
                          <button 
                            onClick={() => { setActiveModalDoc(st); setModalViewMode('detailed'); }} 
                            style={{ width: '100%', backgroundColor: '#0f172a', color: '#ecc94b', border: 'none', padding: '0.3rem 0.6rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.3rem' }}
                          >
                            <Printer size={12} /> PDF
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          /* STATUTES CARDS GRID OR LIST WITH SMOOTH SCROLL PANE */
          <div 
            ref={resultsContainerRef}
            style={{ 
              display: 'grid', 
              gridTemplateColumns: viewMode === 'grid' ? 'repeat(auto-fill, minmax(460px, 1fr))' : '1fr', 
              gap: '1.25rem', 
              marginBottom: '2rem',
              maxHeight: '72vh',
              overflowY: 'auto',
              paddingRight: '6px'
            }}
          >
            {statutesList.map((st: any, idx: number) => {
              const stTitle = st.title || st.name || 'Statutory Record';
              const stJuris = st.jurisdiction || st.province || 'Federal';
              const stNotif = st.notification_number || st.sro_number || '';
              const stInst = st.issuing_institution || '';
              const stDesc = st.short_description || st.detailed_theme || 'Statutory Enactment indexed under ELum Law Repository archives.';
              const badgeInfo = getStatuteBifurcationBadge(st);
              const serialNum = (currentPage - 1) * 30 + (idx + 1);

              return (
                <div 
                  key={st.id || idx}
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    padding: '1.25rem',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    gap: '0.85rem',
                    boxSizing: 'border-box',
                    transition: 'transform 0.15s, box-shadow 0.15s'
                  }}
                >
                  <div>
                    {/* Top Row: S# Badge & Assigned Bifurcation Badge */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem', flexWrap: 'wrap', gap: '0.4rem' }}>
                      <span style={{ display: 'inline-block', backgroundColor: '#0f172a', color: 'white', fontWeight: '800', fontSize: '0.78rem', padding: '0.25rem 0.65rem', borderRadius: '6px' }}>
                        S# {serialNum}
                      </span>
                      <span 
                        style={{ 
                          background: badgeInfo.bg, 
                          color: badgeInfo.color, 
                          border: `1px solid ${badgeInfo.border}`, 
                          padding: '0.25rem 0.65rem', 
                          borderRadius: '6px', 
                          fontSize: '0.72rem', 
                          fontWeight: '800', 
                          letterSpacing: '0.5px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.35rem',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                      >
                        {badgeInfo.label}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.08rem', fontWeight: '800', color: '#0f172a', lineHeight: '1.4' }}>
                      {stTitle}
                    </h3>

                    {/* Metadata Tags */}
                    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.65rem', alignItems: 'center' }}>
                      <span style={{ backgroundColor: '#e0f2fe', color: '#0369a1', fontSize: '0.75rem', fontWeight: '800', padding: '0.2rem 0.55rem', borderRadius: '4px' }}>
                        🏛️ {stJuris}
                      </span>
                      {stNotif && (
                        <span style={{ backgroundColor: '#f3e8ff', color: '#6b21a8', fontSize: '0.75rem', fontWeight: '800', padding: '0.2rem 0.55rem', borderRadius: '4px' }}>
                          🔖 SRO/Notif: {stNotif}
                        </span>
                      )}
                      {stInst && (
                        <span style={{ backgroundColor: '#f0fdf4', color: '#15803d', fontSize: '0.75rem', fontWeight: '800', padding: '0.2rem 0.55rem', borderRadius: '4px' }}>
                          🏢 {stInst}
                        </span>
                      )}
                      {st.year && (
                        <span style={{ backgroundColor: '#fffbeb', color: '#b45309', fontSize: '0.75rem', fontWeight: '800', padding: '0.2rem 0.55rem', borderRadius: '4px' }}>
                          📅 Year {st.year}
                        </span>
                      )}
                    </div>

                    {/* Summary */}
                    <div style={{ backgroundColor: '#f8fafc', padding: '0.65rem 0.85rem', borderRadius: '8px', borderLeft: '3px solid #0284c7', fontSize: '0.85rem', color: '#334155', lineHeight: '1.45' }}>
                      <strong>Brief Outline: </strong>
                      {stDesc}
                    </div>
                  </div>

                  {/* Actions Row */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '0.75rem', borderTop: '1px solid #f1f5f9', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => {
                          setActiveModalDoc(st);
                          setModalViewMode('brief');
                        }}
                        style={{
                          padding: '0.4rem 0.85rem',
                          borderRadius: '6px',
                          border: '1px solid #0284c7',
                          backgroundColor: '#f0f9ff',
                          color: '#0369a1',
                          fontWeight: '700',
                          fontSize: '0.8rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.35rem'
                        }}
                      >
                        <FileText size={14} /> Brief (4-Bullets)
                      </button>

                      <button
                        onClick={() => setQrModalDoc(st)}
                        title="Click to view and scan live QR Code transfer for this statute"
                        style={{
                          padding: '0.4rem 0.85rem',
                          borderRadius: '6px',
                          border: '1px solid #bae6fd',
                          backgroundColor: '#ffffff',
                          color: '#0284c7',
                          fontWeight: '700',
                          fontSize: '0.8rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.35rem'
                        }}
                      >
                        <QrCode size={14} color="#0284c7" /> QR Share
                      </button>

                      <button
                        onClick={() => {
                          setActiveModalDoc(st);
                          setModalViewMode('detailed');
                        }}
                        style={{
                          padding: '0.4rem 0.85rem',
                          borderRadius: '6px',
                          border: 'none',
                          backgroundColor: '#0f172a',
                          color: '#ecc94b',
                          fontWeight: '700',
                          fontSize: '0.8rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.35rem',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                      >
                        <Printer size={14} /> Details & PDF
                      </button>
                    </div>
                  </div>

                </div>
              );
            })}
          </div>
        )}

        {/* PAGINATION CONTROLS */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '2rem' }}>
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              style={{
                padding: '0.6rem 1.2rem',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                backgroundColor: currentPage === 1 ? '#f1f5f9' : 'white',
                color: currentPage === 1 ? '#94a3b8' : '#0f172a',
                fontWeight: '700',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.3rem'
              }}
            >
              <ChevronLeft size={18} /> Previous
            </button>

            <span style={{ fontSize: '0.9rem', fontWeight: '700', color: '#475569' }}>
              Page {currentPage} of {totalPages}
            </span>

            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              style={{
                padding: '0.6rem 1.2rem',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                backgroundColor: currentPage === totalPages ? '#f1f5f9' : 'white',
                color: currentPage === totalPages ? '#94a3b8' : '#0f172a',
                fontWeight: '700',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.3rem'
              }}
            >
              Next <ChevronRight size={18} />
            </button>
          </div>
        )}

      </div>

      {/* FLOATING QUICK SCROLL CONTROLS (SCROLL DOWN & TOP) */}
      <div style={{ position: 'fixed', bottom: '24px', right: '24px', display: 'flex', flexDirection: 'column', gap: '0.5rem', zIndex: 999 }}>
        <button
          onClick={handleScrollDown}
          title="Scroll Down to Results Grid & Pagination"
          style={{
            backgroundColor: '#0284c7',
            color: 'white',
            border: '2px solid white',
            borderRadius: '50%',
            width: '42px',
            height: '42px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(2, 132, 199, 0.4)',
            cursor: 'pointer',
            transition: 'transform 0.15s'
          }}
        >
          <ArrowDown size={20} />
        </button>

        {showScrollTop && (
          <button
            onClick={handleScrollToTop}
            title="Scroll Back to Top"
            style={{
              backgroundColor: '#0f172a',
              color: 'white',
              border: '2px solid white',
              borderRadius: '50%',
              width: '42px',
              height: '42px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(15, 23, 42, 0.4)',
              cursor: 'pointer',
              transition: 'transform 0.15s'
            }}
          >
            <ArrowUp size={20} />
          </button>
        )}
      </div>

      {/* FOOTER WITH COPYRIGHT & E&OE NOTICE */}
      <footer style={{ backgroundColor: '#0f172a', color: '#94a3b8', borderTop: '2px solid #cbd5e1', padding: '1.5rem 2rem', marginTop: '3rem', fontSize: '0.82rem' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            © 2026 <strong>ELum Law Associates</strong>. All Rights Reserved. | <a href="https://www.elumlaw.com" target="_blank" rel="noreferrer" style={{ color: '#ecc94b', textDecoration: 'none', fontWeight: '700' }}>www.elumlaw.com</a>
          </div>
          <div style={{ color: '#f43f5e', fontWeight: '700', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <AlertTriangle size={15} color="#f43f5e" /> Errors & Omissions Excepted (E&OE)
          </div>
        </div>
      </footer>

      {/* DOCUMENT DETAIL MODAL */}
      {activeModalDoc && (
        <DocumentDetailModal
          document={activeModalDoc}
          initialViewMode={modalViewMode}
          onClose={() => setActiveModalDoc(null)}
        />
      )}

      {/* ELUM LAW DICTIONARY MODAL */}
      <BlacksLawDictionaryModal
        isOpen={isDictOpen}
        onClose={() => setIsDictOpen(false)}
      />

      {/* INSTANT STATUTE QR SHARE MODAL */}
      {qrModalDoc && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.85)',
          backdropFilter: 'blur(6px)',
          zIndex: 3000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            maxWidth: '460px',
            width: '100%',
            padding: '1.75rem',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            border: '2px solid #0284c7',
            position: 'relative'
          }}>
            <button
              onClick={() => setQrModalDoc(null)}
              style={{
                position: 'absolute',
                top: '1rem', right: '1rem',
                backgroundColor: '#f1f5f9',
                border: 'none',
                borderRadius: '50%',
                width: '32px', height: '32px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', color: '#64748b'
              }}
            >
              <X size={18} />
            </button>

            <div style={{ textAlign: 'center', marginBottom: '1.25rem' }}>
              <div style={{ backgroundColor: '#e0f2fe', color: '#0369a1', display: 'inline-flex', padding: '0.4rem 0.8rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '800', marginBottom: '0.5rem' }}>
                📷 LIVE SCANNABLE STATUTORY QR CODE
              </div>
              <h2 style={{ fontSize: '1.15rem', fontWeight: '800', color: '#0f172a', margin: '0 0 0.35rem 0', lineHeight: '1.3' }}>
                {qrModalDoc.title || qrModalDoc.name || 'Statute Record'}
              </h2>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748b' }}>
                {qrModalDoc.jurisdiction || 'Federal'} | Year: {qrModalDoc.year || '2026'} {qrModalDoc.notification_number ? `| Ref #: ${qrModalDoc.notification_number}` : ''}
              </p>
            </div>

            {/* QR CODE CONTAINER */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f8fafc',
              borderRadius: '12px',
              padding: '1.25rem',
              border: '1px solid #cbd5e1',
              marginBottom: '1.25rem'
            }}>
              <div style={{ backgroundColor: 'white', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <QRCodeSVG
                  value={typeof window !== 'undefined' ? `${window.location.origin}/statutes?id=${encodeURIComponent(qrModalDoc.id || qrModalDoc._id || qrModalDoc.title)}` : `/statutes?id=${encodeURIComponent(qrModalDoc.id || qrModalDoc._id || qrModalDoc.title)}`}
                  size={180}
                  level="H"
                  includeMargin={true}
                />
              </div>
              <p style={{ margin: '0.75rem 0 0 0', fontSize: '0.78rem', color: '#475569', fontWeight: '600', textAlign: 'center' }}>
                Point mobile camera or WhatsApp to open and share precedent instantly across devices.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={() => {
                  const url = typeof window !== 'undefined' ? `${window.location.origin}/statutes?id=${encodeURIComponent(qrModalDoc.id || qrModalDoc._id || qrModalDoc.title)}` : `/statutes?id=${encodeURIComponent(qrModalDoc.id || qrModalDoc._id || qrModalDoc.title)}`;
                  navigator.clipboard.writeText(url);
                  setCopiedQrLink(true);
                  setTimeout(() => setCopiedQrLink(false), 2500);
                }}
                style={{
                  flex: 1,
                  padding: '0.65rem',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: copiedQrLink ? '#10b981' : '#0284c7',
                  color: 'white',
                  fontWeight: '700',
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.4rem'
                }}
              >
                {copiedQrLink ? <Check size={16} /> : <Copy size={16} />}
                {copiedQrLink ? 'Direct Link Copied!' : 'Copy Direct Link'}
              </button>

              <button
                onClick={() => {
                  const targetDoc = qrModalDoc;
                  setQrModalDoc(null);
                  setActiveModalDoc(targetDoc);
                  setModalViewMode('detailed');
                }}
                style={{
                  padding: '0.65rem 1rem',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  backgroundColor: '#0f172a',
                  color: '#ecc94b',
                  fontWeight: '700',
                  fontSize: '0.85rem',
                  cursor: 'pointer'
                }}
              >
                Open Full Text
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default StatutesDirectory;
